import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useFirmReportData } from '../../../../../api/coreData'
import { useDenaliFilter } from '../../filters/DenaliFilterContextProvider'
import ChloroplethChart from '../../../../charts/echarts/Chloropleth'
import { StateAbbreviationToFull } from './stateIndex'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()

  return useCallback(d => {
    return formatter(d, valueFormat)
  }, [formatter, valueFormat])
}

function DenaliChloropleth ({
  reportId,
  baseQuery,
  height,
  width,
  valueAccessor,
  stateAccessor,
  stateFormat,
  valueFormat,
  seriesName,
  chartProps
}) {
  const { filter, membershipFilter } = useDenaliFilter()
  const query = useMemo(() => {
    return {
      ...baseQuery,
      membershipFilter,
      filters: {
        ...(filter || {}),
        ...(baseQuery?.filters || {})
      },
      reportId
    }
  }, [reportId, baseQuery, filter, membershipFilter])

  const valueFormatter = useFormatters(valueFormat)
  const { data: report, isLoading } = useFirmReportData(query)
  const chartData = useMemo(() => {
    if (!report?.data) return null
    const series = (report?.data || []).map(item => {
      let state = get(item, stateAccessor)
      if (state && stateFormat === 'abbreviation') {
        state = StateAbbreviationToFull[state.toUpperCase()]
      }
      return ({
        name: state,
        value: Number(get(item, valueAccessor))
      })
    })

    const min = series.reduce((prev, cur) => Math.min(cur.value, prev), Number.MAX_SAFE_INTEGER)
    const max = series.reduce((prev, cur) => Math.max(cur.value, prev), Number.MIN_SAFE_INTEGER)

    return {
      name: seriesName,
      series,
      extents: {
        min,
        max
      }
    }
  }, [report, stateAccessor, valueAccessor, stateFormat, seriesName])

  if (isLoading) return null

  return (
    <ChloroplethChart
      height={height}
      width={width}
      data={chartData}
      valueFormat={valueFormatter}
      chartData={chartProps}
    />
  )
}

DenaliChloropleth.propTypes = {
  reportId: PropTypes.string,
  baseQuery: PropTypes.object,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stateAccessor: PropTypes.string,
  stateFormat: PropTypes.oneOf(['abbreviation', 'full']),
  valueAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  seriesName: PropTypes.string,
  chartProps: PropTypes.object
}

DenaliChloropleth.defaultProps = {
  stateFormat: 'full',
  valueFormat: 'marketValue',
  height: 480,
  width: 640,
  seriesName: 'Concentration'
}

export default DenaliChloropleth
